import { RootState } from '@next/core-lib/app';
import { AbstractFormDialogData } from '@next/core-lib/dialog';
import { Currency } from '@next/core-lib/i18n';
import { EntityState } from '@ngrx/entity';

import { SlaughterInvoice, SlaughterReport } from './flock-registration-service.model';
import { ProductTypeEnum } from '../../delivery/models/delivery.model';
import {
  MedicationDataRow,
  SwitchDayDataRow,
  VaccinationDataRow,
} from '../components/animal-group/animal-group.model';
import {
  AdditionalInvoiceDataRow,
  FixedPrice,
} from '../components/final-setup/models/final-setup.model';

export interface CycleModuleState extends RootState {
  cycles?: CyclesState;
  concepts?: Concept[];
  routeTransitionBalance: RouteTransitionBalance;
  hatcheries?: Hatchery[];
  slaughterhouses?: Slaughterhouse[];
  products?: Product[];
  deliveries?: Delivery[];
  slaughterReports?: SlaughterReport[];
  slaughterInvoices?: {
    linked: SlaughterInvoice[];
    unlinked: SlaughterInvoice[];
  };
  selectedProduct?: Product;
  isDetailedOverview: boolean;
  selectedCycleId: number;
  selectedDiscountId?: number;
  isLoaded: boolean;
  farmResources?: FarmResource[];
  cycleResources?: FarmResourcesDivision[];
  carbonFootprint?: CarbonFootprintResponse;
  sustainabilityNoticeVisible: boolean;
}

export type CyclesState = EntityState<Cycle>;

export interface Cycle {
  id?: number;
  cycleNumber: number;
  surfaceArea: number;
  concept: Concept;
  cycleDate?: string;
  cycleEndDate?: string;
  dayOldChicks: DayOldChick[];
  departures: Departure[];
  balancesPerDeliveryLine: BalancePerDeliveryLine[];
  isFinalBalance: boolean;
  costs: Costs;
  currency: Currency;
  observationSettings?: WDOsettings;
  discounts?: Discount[];
  referenceValueSheetId?: number;
  feedAdviceId?: number;
  cycleDescription: string;
  animalGroup?: AnimalGroup;
  finalSetup?: FinalSetup;
  beddingType?: BeddingType;
  beddingAmount?: number;
  transportDistance?: number;
  timeInFreeRange?: number;
}

export enum ObservationSettingsEnum {
  mortality = 'mortality',
  feedIntake = 'feedIntakeInKg',
  waterIntake = 'waterIntakeInL',
  indoorTemperature = 'indoorTemperatureInCelsius',
  outdoorTemperature = 'outdoorTemperatureInCelsius',
  indoorHumidity = 'indoorHumidityInPercentages',
  outdoorHumidity = 'outdoorHumidityInPercentages',
  weight = 'chickWeightInGrams',
  litterQuality = 'litterQuality',
}

export class RouteTransitionBalance {
  balanceRows: BalanceTableRow[];
}

export enum WDOsettingEnum {
  PerDay = 'PerDay',
  PerWeek = 'PerWeek',
  Off = 'Off',
}

export enum DayOfWeekEnum {
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
}

export interface WDOsettings {
  mortality: WDOsettingEnum;
  feedIntake: WDOsettingEnum;
  waterIntake: WDOsettingEnum;
  indoorTemperature: WDOsettingEnum;
  outdoorTemperature: WDOsettingEnum;
  indoorHumidity: WDOsettingEnum;
  outdoorHumidity: WDOsettingEnum;
  weight: WDOsettingEnum;
  litterQuality: WDOsettingEnum;
}

export interface WeekRegistration {
  weekNr: number;
  startDay: number;
  endDay: number;
  barnId: number;
  dayRegistrations: DayRegistration[];
  registrationData: ValueRegistration;
}

export interface Discount {
  id?: number;
  label: DiscountLabelEnum | NweDiscountLabelEnum;
  barn: Barn;
  applicabilityType: ApplicabilityType;
  amount: number;
  linkedSlaughterReportId?: number;
  invoiceDate: string;
  invoiceNumber: string;
  vatPercentage?: number;
  description: string;
  showInFinalSetup?: boolean;
}

export enum DiscountLabelEnum {
  MemberBenefit = 'MemberBenefit',
  MemberDiscount = 'MemberDiscount',
  IntegrationPremium = 'IntegrationPremium',
  NewConstructionPremium = 'NewConstructionPremium',
  NetPresentValueDistribution = 'NetPresentValueDistribution ',
  Other = 'Other',
  Discount = 'Discount',
  BackPayment = 'BackPayment',
  CorrectionFeedPrice = 'CorrectionFeedPrice',
  CompensationPerAgreement = 'CompensationPerAgreement',
}

export enum NweDiscountLabelEnum {
  MemberBenefit = 'Ledenvoordeel',
  MemberDiscount = 'Ledenkorting',
  IntegrationPremium = 'Integratiepremie',
  NewConstructionPremium = 'Nieuwbouwpremie',
  NetPresentValueDistribution = 'NCW',
  Other = 'Overig',
  Discount = 'Korting',
  BackPayment = 'Nabetaling',
  CorrectionFeedPrice = 'CorrectieVoerprijs',
  CompensationPerAgreement = 'VergoedingVolgensAfspraak',
}

export enum ApplicabilityType {
  ArrivalCost = 'ArrivalCost',
  FeedCost = 'FeedCost',
  DepartureRevenue = 'DepartureRevenue',
}

export interface DayRegistration {
  day: number;
  registrationData: ValueRegistration;
}

export interface ValueRegistration {
  mortality?: number;
  selection?: number;
  selectionSmall?: number;
  selectionLegProblems?: number;
  feedIntakeInKg?: number;
  waterIntakeInL?: number;
  feedIntake?: string;
  waterIntake?: string;
  indoorTemperatureInCelsius?: number;
  outdoorTemperatureInCelsius?: number;
  indoorHumidityInPercentages?: number;
  outdoorHumidityInPercentages?: number;
  chickWeightInGrams?: number;
  litterQualityLoosePercentage?: number;
  litterQualityCakedPercentage?: number;
  litterQualityWetPercentage?: number;
}

export interface TableValueRegistration extends ValueRegistration {
  period?: string;
}

export interface Costs {
  water?: number;
  heating?: number;
  electricity?: number;
  cadaver?: number;
  certification?: number;
  healthcare?: number;
  litter?: number;
  catching?: number;
  manure?: number;
  decontamination?: number;
  waterAdditives?: number;
  avined?: number;
  veterinary?: number;
  employment?: number;
  otherRevenue?: number;
  otherCosts?: number;
}

export interface DayOldChick {
  id: number;
  barn: Barn;
  arrivalDate: string;
  breed: Breed;
  parentStockIdentifier?: string;
  hatchery: Hatchery;
  chickBirthDate: string;
  amountOfChicks: number;
  amounts: boolean;
  chicksPrice?: number;
  chicksTotalPrice?: number;
  vaccinationPricePerChick?: number;
  discountPricePerChick?: number;
  diversePricePerChick?: number;
  chicksWeightInGrams?: number;
  chicksParentsAgeInWeeks?: number;
  invoiceDate: string;
  invoiceNumber: string;
  vatPercentage?: number;
}

export interface Breed {
  id: number;
  name: string;
}

export interface Departure {
  id: number;
  barn: Barn;
  type: string;
  slaughterhouse?: Slaughterhouse;
  amountOfChicks: number;
  departureDate: string;
  footpadLesionScore?: number;
  footpadLesionNonePercentage?: number;
  footpadLesionModeratePercentage?: number;
  footpadLesionSeverePercentage?: number;
  heelDermatitis?: number;
  departureWeight?: number;
  slaughterWeight?: number;
  rejectionAtSlaughterhouseInKg?: number;
  rejectionDuringTransportInKg?: number;
  revenue?: number;
  linkedSlaughterReportId?: number;
  invoiceDate: string;
  invoiceNumber: string;
  vatPercentage?: number;
  showInFinalSetup?: boolean;
  revenueSubTotal?: number;
  diverseCosts?: number;
}

export interface Slaughterhouse {
  id: number;
  externalId: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface Hatchery {
  id: number;
  externalId: string;
  name: string;
  address: string;
  postalCode: string;
  city: string;
}

export interface Concept {
  id: number;
  name: string;
  breeds: Breed[];
}

export interface Product {
  externalProductId: string;
  productTitle: string;
  unitVolumeType: string;
  totalUnitsDelivered: number;
  totalUnitsConsumed: number;
  totalUnitsRemaining: number;
  deliveriesFound: Delivery[];
}

export interface Delivery {
  id: number;
  deliveryOrigin: DeliveryOriginEnum;
  externalDeliveryId: string;
  deliveryType: string;
  deliveryDate: string;
  customerId: number;
  shipToId: number;
  deliveryLines: DeliveryLine[];
}

export interface DeliveryLine {
  id: string;
  externalUniqueIdentifier: string;
  externalProductId: string;
  productTitle: string;
  productType: ProductTypeEnum;
  unitVolumeType: string;
  unitsDelivered: number;
  unitsRemaining: number;
  isActive: boolean;
  currency: string;
  unitNettoPrice?: number;
  kgDelivered?: number;
  externalInvoiceId: string;
  invoiceDate: string;
  vatPercentage?: number;
  showInFinalSetup?: boolean;
}

export interface CycleBalanceError {
  balanceRowId: string;
  message: string;
}

export enum DeliveryOriginEnum {
  START = 'start',
  PLATFORM = 'Platform',
  MANUAL = 'Manual',
}

export interface BalanceTableRow {
  balanceRowId: string;
  customerId: number;
  locationId: number;
  cycleId: number;
  deliveryId: number;
  deliveryLineId: string;
  deliveryLineIdentifier: string;
  deliverySource?: DeliveryOriginEnum;
  deliveryDate: Date;
  productIdentifier: string;
  productTitle: string;
  productType: ProductTypeEnum;
  unitsDelivered: number;
  quantity: number;
  unitNettoPrice?: number;
  currency: string;
  balanceInOtherCycles: BalanceInCycle[];
  remainder?: number;
  barnBalanceRows: BarnBalanceRow[];
  isUserDefinedRatio: boolean;
  type?: DeliveryOriginEnum;
  kgDelivered?: number;
  barnId?: number;
  barnName?: string;
  price: number;
  baseQuantity: number;
  nettoAmount: number;
  externalInvoiceId?: string;
  invoiceDate?: string;
  externalDeliveryId?: string;
}

export interface BarnBalanceRow {
  productType: ProductTypeEnum;
  balanceRowId: string;
  barn: Barn;
  amount: number;
  ratio: number;
}

export interface BalanceSummaryRow {
  productTitle: string;
  productIdentifier: string;
  initialStock: number;
  deliveryStock: number;
  finalStock: number;
  totalConsumption: number;
}

export interface FeedConsumptionPerProductType {
  productType: ProductTypeEnum;
  consumption: number;
  consumptionPerDeliveredChick: number;
  price: number;
}

export interface FeedConsumptionSummary {
  consumptionPerProductType: FeedConsumptionPerProductType[];
  totalConsumption: number;
  totalConsumptionPerDeliveredChick: number;
  totalPrice: number;
}

export interface Barn {
  id?: number;
  name: string;
  surfaceArea: number;
  animalGroupBarnIds?: string[];
  isActive?: boolean;
}

export interface AnimalGroupBarnMapping {
  barnId: number;
  barnName: string;
  animalGroupBarnId: string;
}

export interface ClosureFormData extends Costs {
  total: number;
}

export interface BarnDistributionFormInputData extends AbstractFormDialogData {
  balanceTableRows: BalanceTableRow[];
  cycle?: Cycle;
}

export interface BarnDistributionFormResultData {
  balanceTableRows: BalanceTableRow[];
}

export interface VisualInformationBarnBalanceRow {
  productType: ProductTypeEnum;
  balanceRowId: string;
  barn: Barn;
  amount: number;
  ratio: number;
  kgPerChick: number;
  totalChicks: number;
}

export interface VisualInformationInput {
  consumptionInOtherCycles: number;
  availableInThisCycle: number;
  currentRemainder: number;
  consumptionWithinCycle: number;
  balanceRows: VisualInformationBarnBalanceRow[];
}

export enum NotificationType {
  INFO = 'info',
  WARNING = 'warning',
}

export interface BalancePerDeliveryLine {
  customerId: number;
  locationId: number;
  cycleId: number;
  cycleNumber: number;
  deliveryId: number;
  deliveryLineId: string;
  deliveryLineIdentifier: string;
  deliverySource: DeliveryOriginEnum;
  deliveryDate: Date;
  productIdentifier: string;
  productTitle: string;
  productType: ProductTypeEnum;
  unitsDelivered: number;
  kgDelivered: number;
  unitsRemaining: number;
  unitNettoPrice?: number;
  currency: string;
  balanceInOtherCycles: BalanceInCycle[];
  barnBalances: BarnBalance[];
  barnId?: number;
  baseQuantity: number;
  nettoAmount: number;
  externalInvoiceId?: string;
  invoiceDate?: string;
  externalDeliveryId?: string;
}

export interface BalanceInCycle {
  cycleId: number;
  cycleNumber: number;
  amount: number;
}

export interface BarnBalance {
  balanceId: number;
  barnId: number;
  barnAllocationFactor: number;
  amount: number;
  updated: Date;
}

export interface AnimalGroup {
  switchDays?: SwitchDayDataRow[];
  medications?: MedicationDataRow[];
  vaccinations?: VaccinationDataRow[];
  veterinarianId?: string;
  barnMappings: AnimalGroupBarnMapping[];
}

export interface Veterinarian {
  id: string;
  name: string;
}

export interface FinalSetup {
  includeChickDeliveries?: boolean;
  includeFeedDeliveries?: boolean;
  includeDepartures?: boolean;
  includeDiscounts?: boolean;
  cycleFeedDeliveryEndDate?: string;
  additionalInvoices?: AdditionalInvoiceDataRow[];
  fixedPrice?: FixedPrice;
}

export interface FarmResource {
  id: number;
  year: number;
  electricity?: number;
  electricityPhotovoltaic?: number;
  electricityWind?: number;
  naturalGas?: number;
  diesel?: number;
  gasoline?: number;
  coal?: number;
  propaneLpg?: number;
  water?: number;
}

export interface FarmResourcesDivision {
  cycleId: number;
  electricity?: number;
  electricityPhotovoltaic?: number;
  electricityWind?: number;
  naturalGas?: number;
  diesel?: number;
  gasoline?: number;
  coal?: number;
  propaneLpg?: number;
  water?: number;
}

export enum EditMode {
  Add = 'add',
  Edit = 'edit',
}

export enum BeddingType {
  NoBedding = 'NoBedding',
  BarleyStraw = 'BarleyStraw',
  MaizeStover = 'MaizeStover',
  OatStraw = 'OatStraw',
  SawDust = 'SawDust',
  SunflowerHusk = 'SunflowerHusk',
  WheatStraw = 'WheatStraw',
  WoodShavings = 'Woodshavings',
}

export interface CarbonFootprintResponse {
  results: CarbonFootprintResultFound[];
}

export interface CarbonFootprintResultFound {
  id: number;
  customerId: number;
  locationId: number;
  cycleId: number;
  sustellFarmBaselineId: string;
  updated: string;
  carbonFootprintResult: CarbonFootprintResult;
}

export interface CarbonFootprintResult {
  status: ProcessingStatus;
  footprint: Footprint;
}

export enum ProcessingStatus {
  PENDING = 'PENDING',
  FINISHED = 'FINISHED',
}

export interface Footprint {
  impacts: Impact[];
  errors: Error[];
}

export interface Impact {
  name: string;
  value: number;
  unit: string;
}

export interface Error {
  type: string;
  key: string;
  messages: string[];
}
